import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  LinearProgress,
  Grid,
  Paper,
  IconButton,
  TextField,
} from '@mui/material';
import {
  GetApp,
  Close,
  TableChart,
  Description,
  CheckCircle,
} from '@mui/icons-material';
import { exportToCSV, exportToExcel } from '../../hooks/useBulkOperations';
import type { Vehicle, Job, Location } from '../../types/vrp';

interface BulkExportDialogProps {
  open: boolean;
  onClose: () => void;
  entityType: 'vehicles' | 'jobs' | 'locations' | 'optimization_runs' | 'route_summaries' | 'route_steps';
  data: Vehicle[] | Job[] | Location[] | any[];
  projectName?: string;
}

interface ExportOptions {
  format: 'csv' | 'excel';
  includeFields: string[];
  filename: string;
}

const BulkExportDialog: React.FC<BulkExportDialogProps> = ({
  open,
  onClose,
  entityType,
  data,
  projectName = 'project'
}) => {
  const [options, setOptions] = useState<ExportOptions>({
    format: 'csv',
    includeFields: [],
    filename: `${projectName}_${entityType}_${new Date().toISOString().split('T')[0]}`
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);

  // Get available fields based on type
  const getAvailableFields = () => {
    if (!data.length) return [];
    
    const sampleItem = data[0];
    return Object.keys(sampleItem).filter(key => 
      !['created_at', 'updated_at'].includes(key)
    );
  };

  const availableFields = getAvailableFields();

  // Initialize selected fields
  React.useEffect(() => {
    if (availableFields.length > 0 && options.includeFields.length === 0) {
      setOptions(prev => ({
        ...prev,
        includeFields: availableFields
      }));
    }
  }, [availableFields, options.includeFields.length]);

  const handleFieldToggle = (field: string) => {
    setOptions(prev => ({
      ...prev,
      includeFields: prev.includeFields.includes(field)
        ? prev.includeFields.filter(f => f !== field)
        : [...prev.includeFields, field]
    }));
  };

  const handleSelectAll = () => {
    setOptions(prev => ({
      ...prev,
      includeFields: availableFields
    }));
  };

  const handleSelectNone = () => {
    setOptions(prev => ({
      ...prev,
      includeFields: []
    }));
  };

  const handleExport = async () => {
    if (options.includeFields.length === 0) {
      return;
    }

    setIsExporting(true);
    
    try {
      // Filter data to include only selected fields
      const filteredData = data.map(item => {
        const filtered: Record<string, unknown> = {};
        options.includeFields.forEach(field => {
          filtered[field] = (item as Record<string, unknown>)[field];
        });
        return filtered;
      });

      const filename = `${options.filename}.${options.format === 'csv' ? 'csv' : 'xlsx'}`;

      if (options.format === 'csv') {
        exportToCSV(filteredData, filename);
      } else {
        exportToExcel(filteredData, filename);
      }

      setExportComplete(true);
      setTimeout(() => {
        setExportComplete(false);
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const getTypeLabel = () => {
    if (!entityType) return 'Data';
    
    const typeLabels: Record<string, string> = {
      vehicles: 'Vehicles',
      jobs: 'Jobs',
      locations: 'Locations',
      optimization_runs: 'Optimization Runs',
      route_summaries: 'Route Summaries',
      route_steps: 'Route Steps'
    };
    
    return typeLabels[entityType] || entityType.charAt(0).toUpperCase() + entityType.slice(1);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Export {getTypeLabel()}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {data.length === 0 ? (
          <Alert severity="info">
            No {getTypeLabel().toLowerCase()} data available to export.
          </Alert>
        ) : (
          <>
            <Grid container spacing={3}>
              {/* Export Format */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Export Format</InputLabel>
                  <Select
                    value={options.format}
                    onChange={(e) => setOptions(prev => ({
                      ...prev,
                      format: e.target.value as 'csv' | 'excel'
                    }))}
                    label="Export Format"
                  >
                    <MenuItem value="csv">
                      <Box display="flex" alignItems="center">
                        <TableChart sx={{ mr: 1 }} />
                        CSV (Comma Separated Values)
                      </Box>
                    </MenuItem>
                    <MenuItem value="excel">
                      <Box display="flex" alignItems="center">
                        <Description sx={{ mr: 1 }} />
                        Excel Spreadsheet (.xlsx)
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Filename */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Filename"
                  value={options.filename}
                  onChange={(e) => setOptions(prev => ({
                    ...prev,
                    filename: e.target.value
                  }))}
                  helperText={`File will be saved as: ${options.filename}.${options.format === 'csv' ? 'csv' : 'xlsx'}`}
                />
              </Grid>
            </Grid>

            {/* Field Selection */}
            <Box mt={3}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2">
                  Select Fields to Export ({options.includeFields.length} of {availableFields.length} selected)
                </Typography>
                <Box>
                  <Button size="small" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button size="small" onClick={handleSelectNone} sx={{ ml: 1 }}>
                    Select None
                  </Button>
                </Box>
              </Box>

              <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                <Grid container spacing={1}>
                  {availableFields.map((field) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={options.includeFields.includes(field)}
                            onChange={() => handleFieldToggle(field)}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>

            {/* Export Summary */}
            <Box mt={3}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Export Summary:</strong> {data.length} {getTypeLabel().toLowerCase()} records will be exported 
                  with {options.includeFields.length} fields in {options.format.toUpperCase()} format.
                </Typography>
              </Alert>
            </Box>

            {/* Export Progress */}
            {isExporting && (
              <Box mt={2}>
                <Typography variant="body2" gutterBottom>
                  Preparing export...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {/* Export Complete */}
            {exportComplete && (
              <Box mt={2}>
                <Alert severity="success" icon={<CheckCircle />}>
                  Export completed successfully! File has been downloaded.
                </Alert>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        {data.length > 0 && (
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={isExporting || options.includeFields.length === 0}
            startIcon={<GetApp />}
          >
            {isExporting ? 'Exporting...' : `Export ${data.length} Records`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkExportDialog;
